/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Avatar, Typography } from '@coze-arch/coze-design';
import {
  IconCozArrowDown,
  IconCozMagnifier,
  IconCozCheckMark,
} from '@coze-arch/coze-design/icons';
import { useSpaceList, useSpaceStore } from '@adp-foundation/space-store';
import { I18n } from '@adp-arch/i18n';
import { localStorageService } from '@adp-foundation/local-storage';

export const WorkspaceSwitcher = () => {
  const navigate = useNavigate();
  const { spaces = [] } = useSpaceList();
  const currentSpace = useSpaceStore(state => state.space);
  const [searchValue, setSearchValue] = useState('');
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) {
      return;
    }
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setVisible(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible]);

  const filteredSpaces = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();
    return spaces.filter(space => {
      const matchKeyword =
        !keyword ||
        (space.name?.toLowerCase().includes(keyword) ?? false) ||
        String(space.id).toLowerCase().includes(keyword);
      return matchKeyword;
    });
  }, [spaces, searchValue]);

  const handleSwitch = useCallback(
    (spaceId: string) => {
      setVisible(false);
      localStorageService.setValue('workspace-spaceId', spaceId);
      navigate(`/space/${spaceId}`);
    },
    [navigate],
  );

  return (
    <div className="relative" ref={containerRef}>
      <div
        className="px-5 pb-3 flex items-center h-[52px] cursor-pointer hover:bg-[var(--coz-bg-plus)] transition-colors"
        role="button"
        onClick={() => setVisible(prev => !prev)}
      >
        <Typography.Title
          heading={6}
          ellipsis={{ showTooltip: true }}
          style={{ margin: 0 }}
          className="flex-1"
        >
          {currentSpace?.name || I18n.t('menu_title_personal_space')}
        </Typography.Title>
        <IconCozArrowDown className="ml-2" />
      </div>
      {visible ? (
        <div className="absolute left-0 top-[calc(100%+8px)] z-10 w-[220px] flex flex-col max-h-[420px] coz-bg-max rounded-2xl shadow-lg">
          <div className="px-3 pt-3 pb-2">
            <Input
              prefix={<IconCozMagnifier />}
              placeholder="Search workspace"
              value={searchValue}
              onChange={value => setSearchValue(value)}
            />
          </div>
          <div className="flex-1 overflow-y-auto min-h-[140px]">
            <div className="px-3 py-1 text-xs text-[var(--coz-fg-tertiary)]">
              Recent
            </div>
            {filteredSpaces.length === 0 ? (
              <div className="px-3 py-8 text-center">
                <Typography.Text type="tertiary">
                  No workspace found
                </Typography.Text>
              </div>
            ) : (
              filteredSpaces.map((space, index) => {
                const isActive = currentSpace?.id === space.id;
                return (
                  <div
                    key={space.id}
                    className={`flex items-center px-3 py-2 cursor-pointer text-left transition-colors ${
                      isActive
                        ? 'bg-[var(--coz-bg-tertiary)]'
                        : 'hover:bg-[var(--coz-bg-tertiary)]'
                    }`}
                    onClick={() => space.id && handleSwitch(space.id)}
                  >
                    <div className="w-5 mr-2 flex justify-center">
                      {isActive ? (
                        <IconCozCheckMark style={{ color: 'var(--coz-fg-primary)' }} />
                      ) : null}
                    </div>
                    <Avatar size="small" src={(space as any).icon_url} name={space.name ?? ''} className="mr-2" />
                    <div className="flex-1 truncate text-sm">{space.name}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};